import type { ParsedItem, ParsedGem, GemGroup } from './pob-parser';
import { aggregatePseudos, PSEUDOS, findStatMultiline, findOptionId, extractValue, extractDamageRange, isDamageRangeStat, hasInversePolarity } from './stat-matcher';

export interface EditableFilter {
  id: string;                                 // stat ID (e.g. 'explicit.stat_1234')
  label: string;                              // user-facing name
  kind: 'pseudo' | 'explicit';
  isOption: boolean;                          // option-based (dropdown) vs numeric
  optionValue?: string;                       // the option ID if isOption
  optionText?: string;                        // readable option name
  min?: number | null;                        // numeric min
  max?: number | null;                        // numeric max
  inverse: boolean;                           // true if reduced/less mod (uses max instead of min)
  disabled: boolean;
  originalVal: number | null;                 // raw rolled value from PoB
}

export interface BuildResult {
  url: string;
  league: string;
  // Editable filter list — UI can mutate these values and call rebuildUrl()
  filters: EditableFilter[];
  unmatched: string[];
  // Non-filter parts of the query (preserved across rebuilds)
  queryBase: Record<string, unknown>;
  // Legacy fields for backwards-compat with main.ts display
  pseudos: Array<{ pk: string; sid: string; total: number; minVal: number }>;
  explicits: Array<{ text: string; sid: string; cat: string; val: number | null; filterVal: number | null; inverse: boolean }>;
}

export function rebuildUrl(league: string, queryBase: Record<string, unknown>, filters: EditableFilter[]): string {
  const statFilters: StatFilter[] = filters.map(f => {
    const sf: StatFilter = { id: f.id, disabled: f.disabled };
    if (f.isOption && f.optionValue) {
      sf.value = { option: f.optionValue };
    } else {
      const v: { min?: number; max?: number } = {};
      if (f.min != null) v.min = f.min;
      if (f.max != null) v.max = f.max;
      if (v.min !== undefined || v.max !== undefined) sf.value = v;
    }
    return sf;
  });

  const query = JSON.parse(JSON.stringify(queryBase)) as Record<string, unknown>;
  const qInner = query.query as Record<string, unknown>;
  if (statFilters.length) {
    qInner.stats = [{ type: 'and', filters: statFilters.slice(0, 25), disabled: false }];
  } else {
    delete qInner.stats;
  }
  return `https://www.pathofexile.com/trade/search/${encodeURIComponent(league)}?q=${encodeURIComponent(JSON.stringify(query))}`;
}

const SLOT_CATEGORY: Record<string, string> = {
  'Helmet': 'armour.helmet',
  'Body Armour': 'armour.chest',
  'Gloves': 'armour.gloves',
  'Boots': 'armour.boots',
  'Shield': 'armour.shield',
  'Quiver': 'armour.quiver',
  'Belt': 'accessory.belt',
  'Amulet': 'accessory.amulet',
  'Ring 1': 'accessory.ring',
  'Ring 2': 'accessory.ring',
  'Flask 1': 'flask', 'Flask 2': 'flask', 'Flask 3': 'flask',
  'Flask 4': 'flask', 'Flask 5': 'flask',
};

const WEAPON_KEYS: Array<[string, string]> = [
  ['Two Hand Sword', 'weapon.twosword'], ['One Hand Sword', 'weapon.onesword'],
  ['Two Hand Axe', 'weapon.twoaxe'], ['One Hand Axe', 'weapon.oneaxe'],
  ['Two Hand Mace', 'weapon.twomace'], ['One Hand Mace', 'weapon.onemace'],
  ['Rune Dagger', 'weapon.runedagger'], ['Dagger', 'weapon.dagger'],
  ['Warstaff', 'weapon.warstaff'], ['Staff', 'weapon.staff'],
  ['Bow', 'weapon.bow'], ['Wand', 'weapon.wand'],
  ['Sceptre', 'weapon.sceptre'], ['Claw', 'weapon.claw'],
];

const JEWEL_ABYSS = new Set(['Ghastly Eye Jewel', 'Hypnotic Eye Jewel', 'Murderous Eye Jewel', 'Searching Eye Jewel']);
const JEWEL_CLUSTER = new Set(['Small Cluster Jewel', 'Medium Cluster Jewel', 'Large Cluster Jewel']);

// PoB strips non-ASCII chars from unique names; trade API needs the originals.
const UNIQUE_NAME_FIXUPS: Record<string, string> = {
  'Mjolner': 'Mjölner',
  'Doppelganger Guise': 'Doppelgänger Guise',
  'Maelstrom of Chaos': 'Maelström of Chaos',
};

function getCategory(slot: string, base: string): string | null {
  if (SLOT_CATEGORY[slot]) return SLOT_CATEGORY[slot];
  if (slot.startsWith('Weapon')) {
    for (const [k, cat] of WEAPON_KEYS) if (base.includes(k)) return cat;
  }
  return null;
}

function getJewelCategory(base: string): string | null {
  if (!base) return null;
  if (JEWEL_CLUSTER.has(base)) return 'jewel.cluster';
  if (JEWEL_ABYSS.has(base)) return 'jewel.abyss';
  return 'jewel';
}

function buildUrl(league: string, query: object): string {
  return `https://www.pathofexile.com/trade/search/${encodeURIComponent(league)}?q=${encodeURIComponent(JSON.stringify(query))}`;
}

interface StatFilter {
  id: string;
  value?: { min?: number; max?: number; option?: string };
  disabled: boolean;
}

const PSEUDO_LABELS: Record<string, string> = {
  life: 'Total Life', es: 'Total ES', mana: 'Total Mana',
  str: 'Total Str', dex: 'Total Dex', int: 'Total Int',
  fire_res: 'Total Fire Res', cold_res: 'Total Cold Res',
  lightning_res: 'Total Lightning Res', chaos_res: 'Total Chaos Res',
  elemental_res: 'Total Ele Res', life_regen: 'Life Regen/sec',
  all_attr: 'All Attr',
};

function collectFilters(
  explicits: string[],
  implicits: string[],
  minRatio: number,
  preferOverride?: string[],
): {
  filtersList: StatFilter[];
  editable: EditableFilter[];
  pseudos: BuildResult['pseudos'];
  explicitsDisplay: BuildResult['explicits'];
  unmatched: string[];
} {
  const pAll = aggregatePseudos(explicits);
  const pImp = aggregatePseudos(implicits);
  const totals = { ...pAll.totals };
  for (const k in pImp.totals) totals[k] = (totals[k] ?? 0) + pImp.totals[k];

  const filtersList: StatFilter[] = [];
  const editable: EditableFilter[] = [];
  const pseudos: BuildResult['pseudos'] = [];

  for (const pk in totals) {
    const total = totals[pk];
    if (total === 0) continue;
    const sid = PSEUDOS[pk];
    if (!sid) continue;
    const minVal = Math.max(1, Math.floor(total * minRatio));
    // All filters default OFF — user enables via checkbox
    filtersList.push({ id: sid, value: { min: minVal }, disabled: true });
    pseudos.push({ pk, sid, total, minVal });
    editable.push({
      id: sid,
      label: PSEUDO_LABELS[pk] ?? pk,
      kind: 'pseudo',
      isOption: false,
      min: minVal,
      max: null,
      inverse: false,
      disabled: true,
      originalVal: Math.round(total),
    });
  }

  const usedIds = new Set<string>();
  const explicitsDisplay: BuildResult['explicits'] = [];
  const unmatched: string[] = [];

  const processMods = (mods: string[], unmappedSet: string[], defaultPrefer: string[]) => {
    // Build a filtered list of just the unmapped mods (preserve raw for tag lookup)
    const unmappedMods: string[] = [];
    const rawMods: string[] = [];
    for (const raw of mods) {
      const cleaned = raw.replace(/\{[^}]*\}/g, '').trim();
      if (!cleaned || cleaned.startsWith('Quality') || cleaned.startsWith('Sockets')) continue;
      if (!unmappedSet.includes(cleaned)) continue;
      unmappedMods.push(cleaned);
      rawMods.push(raw);
    }

    let i = 0;
    while (i < unmappedMods.length) {
      const raw = rawMods[i];
      const tags = Array.from(raw.matchAll(/\{([^}]+)\}/g)).map(m => m[1]);

      // If caller forced a prefer order (e.g. cluster jewels), use it regardless of tags
      let prefer = defaultPrefer;
      if (!preferOverride) {
        if (tags.some(t => t.includes('crafted'))) prefer = ['crafted', 'explicit', 'fractured', 'implicit', 'enchant'];
        else if (tags.some(t => t.includes('enchant'))) prefer = ['enchant', 'implicit', 'explicit'];
        else if (tags.some(t => t.includes('fractured'))) prefer = ['fractured', 'explicit', 'crafted', 'implicit'];
      }

      // Multi-line match: try joining 1-4 consecutive mod lines (greedy, longest first)
      const multi = findStatMultiline(unmappedMods, i, prefer);
      if (multi) {
        const { entry, category, capturedOption } = multi.match;
        const joinedText = multi.joinedText;
        if (!usedIds.has(entry.id)) {
          usedIds.add(entry.id);
          const optionId = findOptionId(entry, joinedText, capturedOption);
          const shortText = joinedText.replace(/\n/g, ' / ');
          if (optionId !== null) {
            // Options-based stats (Allocates X, Passive in Radius of X) are fixed identifiers —
            // enabled by default, user can't tune the value, only toggle off
            filtersList.push({ id: entry.id, value: { option: String(optionId) }, disabled: false });
            explicitsDisplay.push({ text: joinedText, sid: entry.id, cat: category, val: null, filterVal: null, inverse: false });
            const optEntry = entry.options?.find(o => String(o.id) === String(optionId));
            editable.push({
              id: entry.id,
              label: shortText,
              kind: 'explicit',
              isOption: true,
              optionValue: String(optionId),
              optionText: optEntry?.text,
              min: null, max: null, inverse: false,
              disabled: false,
              originalVal: null,
            });
          } else {
            // Damage-range mods ("Adds X to Y Lightning Damage to Spells"): trade filter
            // value is the AVERAGE of the two rolls, not the first number alone.
            const dmgRange = isDamageRangeStat(entry.text) ? extractDamageRange(joinedText) : null;
            const val = dmgRange ? dmgRange.avg : extractValue(joinedText);
            const inverse = hasInversePolarity(joinedText);
            const isExactSeed = entry.id.includes('pseudo_timeless_jewel_') || entry.id.includes('scourge_') || /seed/i.test(entry.id);
            // Cluster-jewel passive-count stats: exact integer counts, never scale
            const isClusterCount = entry.text === 'Adds # Passive Skills' ||
                                   entry.text === '# Added Passive Skills are Jewel Sockets';
            if (val !== null && val !== 0) {
              const abs = (isExactSeed || isClusterCount)
                ? Math.floor(Math.abs(val))
                : Math.max(1, Math.floor(Math.abs(val) * minRatio));
              if (inverse) {
                filtersList.push({ id: entry.id, value: { max: -abs }, disabled: true });
                explicitsDisplay.push({ text: joinedText, sid: entry.id, cat: category, val, filterVal: -abs, inverse: true });
                editable.push({
                  id: entry.id, label: shortText, kind: 'explicit', isOption: false,
                  min: null, max: -abs, inverse: true, disabled: true, originalVal: Math.round(val),
                });
              } else if (dmgRange) {
                // Damage-range: prefill BOTH min (scaled lo) and max (scaled hi) inputs.
                const loScaled = Math.max(1, Math.floor(dmgRange.lo * minRatio));
                const hiScaled = Math.max(1, Math.floor(dmgRange.hi * minRatio));
                filtersList.push({ id: entry.id, value: { min: loScaled, max: hiScaled }, disabled: true });
                explicitsDisplay.push({ text: joinedText, sid: entry.id, cat: category, val, filterVal: loScaled, inverse: false });
                editable.push({
                  id: entry.id, label: shortText, kind: 'explicit', isOption: false,
                  min: loScaled, max: hiScaled, inverse: false, disabled: true, originalVal: Math.round(val),
                });
              } else {
                filtersList.push({ id: entry.id, value: { min: abs }, disabled: true });
                explicitsDisplay.push({ text: joinedText, sid: entry.id, cat: category, val, filterVal: abs, inverse: false });
                editable.push({
                  id: entry.id, label: shortText, kind: 'explicit', isOption: false,
                  min: abs, max: null, inverse: false, disabled: true, originalVal: Math.round(val),
                });
              }
            } else {
              filtersList.push({ id: entry.id, disabled: true });
              explicitsDisplay.push({ text: joinedText, sid: entry.id, cat: category, val: null, filterVal: null, inverse: false });
              editable.push({
                id: entry.id, label: shortText, kind: 'explicit', isOption: false,
                min: null, max: null, inverse: false, disabled: true, originalVal: null,
              });
            }
          }
        }
        i += multi.consumed;
      } else {
        unmatched.push(unmappedMods[i]);
        i++;
      }
    }
  };

  processMods(explicits, pAll.unmapped, preferOverride ?? ['explicit', 'fractured', 'crafted', 'implicit', 'enchant']);
  processMods(implicits, pImp.unmapped, preferOverride ?? ['implicit', 'explicit', 'enchant', 'crafted']);

  return { filtersList, editable, pseudos, explicitsDisplay, unmatched };
}

export function linkUnique(league: string, item: ParsedItem, minRatio = 0.9): BuildResult {
  const { editable, pseudos, explicitsDisplay, unmatched } = collectFilters(
    item.explicits, item.implicits, minRatio
  );

  // Foulborn-prefixed uniques: strip "Foulborn " from name, set mutated flag
  let tradeName = item.name;
  let isFoulborn = false;
  if (tradeName.startsWith('Foulborn ')) {
    tradeName = tradeName.substring('Foulborn '.length);
    isFoulborn = true;
  }
  if (UNIQUE_NAME_FIXUPS[tradeName]) tradeName = UNIQUE_NAME_FIXUPS[tradeName];

  const miscFilters: Record<string, unknown> = {
    mirrored: { option: 'false' },
    corrupted: { option: item.corrupted ? 'true' : 'false' },
  };
  if (isFoulborn) miscFilters.mutated = { option: 'true' };

  const queryBase: Record<string, unknown> = {
    query: {
      status: { option: 'securable' },
      name: tradeName,
      ...(item.base ? { type: item.base } : {}),
      filters: {
        misc_filters: { filters: miscFilters, disabled: false },
      },
    },
    sort: { price: 'asc' },
  };
  const url = rebuildUrl(league, queryBase, editable);
  return { url, league, filters: editable, queryBase, pseudos, explicits: explicitsDisplay, unmatched };
}

export function linkRare(league: string, slot: string, item: ParsedItem, minRatio = 0.85): BuildResult {
  const { editable, pseudos, explicitsDisplay, unmatched } = collectFilters(
    item.explicits, item.implicits, minRatio
  );

  const category = getCategory(slot, item.base);
  const typeFilters: Record<string, unknown> = { rarity: { option: 'nonunique' } };
  if (category) typeFilters.category = category;

  const filtersRoot: Record<string, unknown> = {
    type_filters: { filters: typeFilters, disabled: false },
    misc_filters: {
      filters: {
        mirrored: { option: 'false' },
        corrupted: { option: item.corrupted ? 'true' : 'false' },
      },
      disabled: false,
    },
  };

  if (Object.keys(item.defense).length && ['Helmet', 'Body Armour', 'Gloves', 'Boots', 'Shield'].includes(slot)) {
    const armourF: Record<string, { min: number }> = {};
    for (const k of ['ar', 'es', 'ev'] as const) {
      if (item.defense[k] != null) armourF[k] = { min: Math.floor(item.defense[k]! * minRatio) };
    }
    if (Object.keys(armourF).length) {
      filtersRoot.armour_filters = { filters: armourF, disabled: false };
    }
  }

  const queryBase: Record<string, unknown> = {
    query: {
      status: { option: 'securable' },
      filters: filtersRoot,
      ...(item.base ? { type: item.base } : {}),
    },
    sort: { price: 'asc' },
  };
  const url = rebuildUrl(league, queryBase, editable);
  return { url, league, filters: editable, queryBase, pseudos, explicits: explicitsDisplay, unmatched };
}

export function linkMagicFlask(league: string, item: ParsedItem): BuildResult {
  const { editable, pseudos, explicitsDisplay, unmatched } = collectFilters(
    item.explicits, item.implicits, 0.85
  );
  const queryBase: Record<string, unknown> = {
    query: {
      status: { option: 'securable' },
      ...(item.base ? { type: item.base } : {}),
      filters: {
        type_filters: {
          filters: { category: 'flask', rarity: { option: 'magic' } },
          disabled: false,
        },
        misc_filters: {
          filters: { mirrored: { option: 'false' }, corrupted: { option: 'false' } },
          disabled: false,
        },
      },
    },
    sort: { price: 'asc' },
  };
  const url = rebuildUrl(league, queryBase, editable);
  return { url, league, filters: editable, queryBase, pseudos, explicits: explicitsDisplay, unmatched };
}

export function linkMagic(league: string, slot: string, item: ParsedItem, minRatio = 0.85): BuildResult {
  const { editable, pseudos, explicitsDisplay, unmatched } = collectFilters(
    item.explicits, item.implicits, minRatio
  );

  const category = getCategory(slot, item.base);
  const typeFilters: Record<string, unknown> = { rarity: { option: 'magic' } };
  if (category) typeFilters.category = category;

  const filtersRoot: Record<string, unknown> = {
    type_filters: { filters: typeFilters, disabled: false },
    misc_filters: {
      filters: {
        mirrored: { option: 'false' },
        corrupted: { option: item.corrupted ? 'true' : 'false' },
      },
      disabled: false,
    },
  };

  if (Object.keys(item.defense).length && ['Helmet', 'Body Armour', 'Gloves', 'Boots', 'Shield'].includes(slot)) {
    const armourF: Record<string, { min: number }> = {};
    for (const k of ['ar', 'es', 'ev'] as const) {
      if (item.defense[k] != null) armourF[k] = { min: Math.floor(item.defense[k]! * minRatio) };
    }
    if (Object.keys(armourF).length) {
      filtersRoot.armour_filters = { filters: armourF, disabled: false };
    }
  }

  const queryBase: Record<string, unknown> = {
    query: {
      status: { option: 'securable' },
      filters: filtersRoot,
      ...(item.base ? { type: item.base } : {}),
    },
    sort: { price: 'asc' },
  };
  const url = rebuildUrl(league, queryBase, editable);
  return { url, league, filters: editable, queryBase, pseudos, explicits: explicitsDisplay, unmatched };
}

export function linkJewel(league: string, item: ParsedItem, minRatio = 0.8): BuildResult {
  if (item.rarity === 'UNIQUE' || item.rarity === 'RELIC') {
    return linkUnique(league, item, minRatio);
  }
  const category = getJewelCategory(item.base);
  const isCluster = category === 'jewel.cluster';
  const isMediumCluster = item.base === 'Medium Cluster Jewel';
  const { editable, pseudos, explicitsDisplay, unmatched } = collectFilters(
    item.explicits, item.implicits, minRatio,
    isCluster ? ['enchant', 'explicit', 'crafted', 'implicit'] : undefined,
  );

  // Medium Cluster Jewels: for "Adds # Passive Skills" specifically, set max to allow
  // upgrade rolls. Cap at 5 (medium-cluster max passive count is 6, but 4-passive medium
  // jewels are usable as 5-passive). Rule: max = max(value, 5).
  // → value 4 → max 5, value 5 → max 5, value 6 → max 6.
  if (isMediumCluster) {
    for (const f of editable) {
      if (f.id.endsWith('stat_3086156145') && f.originalVal != null && !f.isOption) {
        f.max = Math.max(f.originalVal, 5);
      }
    }
  }

  const typeFilters: Record<string, unknown> = {};
  if (item.rarity === 'RARE' || item.rarity === 'MAGIC') {
    typeFilters.rarity = { option: item.rarity.toLowerCase() };
  }
  if (category) typeFilters.category = category;

  const queryBase: Record<string, unknown> = {
    query: {
      status: { option: 'securable' },
      ...(item.base ? { type: item.base } : {}),
      filters: {
        type_filters: { filters: typeFilters, disabled: false },
        misc_filters: {
          filters: {
            mirrored: { option: 'false' },
            corrupted: { option: item.corrupted ? 'true' : 'false' },
          },
          disabled: false,
        },
      },
    },
    sort: { price: 'asc' },
  };
  const url = rebuildUrl(league, queryBase, editable);
  return { url, league, filters: editable, queryBase, pseudos, explicits: explicitsDisplay, unmatched };
}

export function linkGem(league: string, gem: ParsedGem): string {
  const category = gem.isSupport || gem.isAwakened ? 'gem.supportgem' : 'gem.activegem';

  // Transfigured gems: variantId ends with AltX/AltY → trade uses base name + `disc: alt_x/alt_y`
  let tradeName = gem.name;
  let disc: string | null = null;
  const altMatch = gem.variantId.match(/Alt([XY])$/);
  if (altMatch) {
    disc = 'alt_' + altMatch[1].toLowerCase(); // "alt_x" or "alt_y"
    // Strip " of Something" suffix — trade type is just the base gem name
    tradeName = gem.name.replace(/\s+of\s+.+$/, '').replace(/\s+Support$/, '');
  }

  // Trade requires " Support" suffix for support gems (PoB omits it)
  if ((gem.isSupport || gem.isAwakened) && !tradeName.endsWith(' Support')) {
    tradeName = tradeName + ' Support';
  }

  // Corrupted filter: always "any" for gems — user wants both normal and corrupted listings.
  const miscFilters: Record<string, unknown> = {
    mirrored: { option: 'false' },
  };
  // Use EXACT level/quality as min — don't look for "less" gems.
  if (gem.level >= 1) (miscFilters as any).gem_level = { min: gem.level };
  if (gem.quality >= 1) (miscFilters as any).quality = { min: gem.quality };

  const query: Record<string, unknown> = {
    query: {
      status: { option: 'securable' },
      type: disc ? { option: tradeName, disc } : tradeName,
      filters: {
        type_filters: {
          filters: { category },
          disabled: false,
        },
        misc_filters: { filters: miscFilters, disabled: false },
      },
    },
    sort: { price: 'asc' },
  };

  return buildUrl(league, query);
}

export function buildLinks(
  league: string,
  equipped: Array<{ slot: string; item: ParsedItem }>,
  jewels: ParsedItem[],
  gemGroups: GemGroup[] = [],
  minRatio = 0.85,
): {
  equipment: Array<{ slot: string; item: ParsedItem; result: BuildResult }>;
  jewels: Array<{ item: ParsedItem; result: BuildResult }>;
  gemGroups: Array<{ group: GemGroup; gems: Array<{ gem: ParsedGem; url: string }> }>;
} {
  const equipment = equipped.map(({ slot, item }) => {
    let result: BuildResult;
    if (item.rarity === 'UNIQUE' || item.rarity === 'RELIC') {
      result = linkUnique(league, item, minRatio);
    } else if (item.rarity === 'MAGIC' && slot.startsWith('Flask')) {
      result = linkMagicFlask(league, item);
    } else if (item.rarity === 'RARE') {
      result = linkRare(league, slot, item, minRatio);
    } else if (item.rarity === 'MAGIC') {
      result = linkMagic(league, slot, item, minRatio);
    } else {
      result = linkMagicFlask(league, item);
    }
    return { slot, item, result };
  });

  const jewelResults = jewels.map(item => ({ item, result: linkJewel(league, item, minRatio) }));
  const gemGroupResults = gemGroups.map(group => ({
    group,
    gems: group.gems.map(gem => ({ gem, url: linkGem(league, gem) })),
  }));

  return { equipment, jewels: jewelResults, gemGroups: gemGroupResults };
}
